import { Button, Modal } from '@imprivata-cloud/components';
import { Form } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getEntraIdConsentUrl } from '../../../api/portalServices';
import { useSession } from '../../../common/authorization/context/Session';
import { ErrorCode } from '../../../errorHandler/constants';
import { AppError } from '../../../errorHandler/errors';
import ConfigEntraID from '../entraId/configEntraID';
import './UsersEmptyPage.less';
import { type Directory, SyncStatus } from '../../../api/types';
import { useErrorContext } from '../../../errorHandler/context/ErrorContext';
import GroupsModal from './groups/GroupsModal';

export interface UsersEmptyPageProps {
  directories?: Directory[];
  initSyncStatus?: SyncStatus;
}

const UsersEmptyPage = ({ directories, initSyncStatus }: UsersEmptyPageProps) => {
  const { t } = useTranslation();
  const { emitError } = useErrorContext();
  const [addEntraIdVisible, setAddEntraIdVisible] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [entraIdForm] = Form.useForm();
  const entraIdValue = useWatch('entraId', { form: entraIdForm });
  const isEntraIdValid = Boolean(entraIdValue);
  const { tenantId } = useSession();

  const redirectToMicrosoft = async () => {
    try {
      const entraId = entraIdForm.getFieldValue('entraId');
      const consentUrlData = await getEntraIdConsentUrl({ entraTenantId: entraId }, tenantId ?? '');
      if (consentUrlData?.entraConsentUrl) {
        window.location.href = consentUrlData?.entraConsentUrl;
      } else {
        console.error('Error fetching Entra Id consent url:', consentUrlData);
        emitError(
          new AppError({ code: ErrorCode.UNKNOWN, message: 'Error fetching Entra Id consent url' }),
        );
      }
    } catch (error) {
      console.error('Error fetching Entra Id consent url:', error);
      emitError(error as AppError);
    }
  };

  return (
    <>
      <div className={'add-entra-id-container'}>
        {t('portal.settings.identity-users.no-users')}
        {(!directories || !directories[0]) && (
          <div>
            <Button type={'link'} onClick={() => setAddEntraIdVisible(true)}>
              {t('portal.settings.users.empty.add-entra-id')}
            </Button>
          </div>
        )}
        {directories &&
          directories[0]?.syncedGroupCount === 0 &&
          (initSyncStatus === SyncStatus.RUNNING ? (
            <p>{t('portal.settings.users.empty.sync-in-progress')}</p>
          ) : (
            <div>
              <Button
                type={'link'}
                onClick={() => setGroupsOpen(true)}
                label={t('portal.settings.users.empty.add-groups')}
              />
            </div>
          ))}
      </div>
      {groupsOpen && (
        <GroupsModal
          open={groupsOpen}
          onClose={() => setGroupsOpen(false)}
          tenantId={tenantId || ''}
          initSyncStatus={initSyncStatus}
        />
      )}
      <Modal
        open={addEntraIdVisible}
        title={t('portal.config-entra-id.title')}
        width={650}
        onOk={redirectToMicrosoft}
        okText={t('portal.config-entra-id.continue-to-microsoft')}
        okButtonProps={{ disabled: !isEntraIdValid }}
        onCancel={() => setAddEntraIdVisible(false)}
        maskClosable={false}
      >
        <ConfigEntraID tenantId={tenantId ?? ''} entraIdForm={entraIdForm} />
      </Modal>
    </>
  );
};

export default UsersEmptyPage;
